import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, User, Menu, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../hooks/useLanguage';
import { Button } from '../ui/Button';
export function Navbar() {
  const {
    user,
    logout,
    isAuthenticated
  } = useAuth();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const handleLanguageChange = (lang: 'ar' | 'en') => {
    setLanguage(lang);
    setIsLangOpen(false);
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <nav className="sticky top-0 z-50 w-full bg-white backdrop-blur-md border-b border-gray-200 shadow-sm" dir={language === 'ar' ? 'rtl' : 'ltr'} data-id="element-251">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-252">
        <div className="flex justify-between items-center h-16" data-id="element-253">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-id="element-254">
            <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 w-auto" data-id="element-255" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6" data-id="element-256">
            {/* Language Switcher - Dropdown Style */}
            <div className="relative" ref={langRef} data-id="element-257">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-2 px-2 py-1.5 text-gray-600 hover:text-[#4f1964] transition-colors" data-id="element-258">
                <span className="text-lg" data-id="element-259">
                  {language === 'ar' ? '🇸🇦' : '🇺🇸'}
                </span>
                <span className="text-sm font-medium" data-id="element-260">
                  {language === 'ar' ? 'العربية' : 'English'}
                </span>
                <ChevronDown size={16} className={`transition-transform ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-261" />
              </button>

              <AnimatePresence data-id="element-262">
                {isLangOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.15
              }} className="absolute top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden min-w-[140px]" style={{
                [language === 'ar' ? 'right' : 'left']: 0
              }} data-id="element-263">
                    <button onClick={() => handleLanguageChange('ar')} className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${language === 'ar' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-264">
                      <span className="text-lg" data-id="element-265">🇸🇦</span>
                      <span data-id="element-266">العربية</span>
                    </button>
                    <button onClick={() => handleLanguageChange('en')} className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${language === 'en' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-267">
                      <span className="text-lg" data-id="element-268">🇺🇸</span>
                      <span data-id="element-269">English</span>
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {isAuthenticated ? <>
                <Link to="/dashboard" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-270">
                  {t('nav.dashboard')}
                </Link>
                <div className={`flex items-center gap-4 ${language === 'ar' ? 'mr-4 border-r pr-4' : 'ml-4 border-l pl-4'} border-gray-200`} data-id="element-271">
                  <div className="flex items-center gap-2" data-id="element-272">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-[#4f1964]" data-id="element-273">
                      <User size={16} data-id="element-274" />
                    </div>
                    <span className="text-sm font-medium text-gray-700" data-id="element-275">
                      {user?.name}
                    </span>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-100" data-id="element-276">
                    <LogOut size={16} className={language === 'ar' ? 'ml-2' : 'mr-2'} data-id="element-277" />
                    {t('nav.logout')}
                  </Button>
                </div>
              </> : <div className="flex items-center gap-3" data-id="element-278">
                <Link to="/login" data-id="element-279">
                  <Button variant="outline" size="sm" data-id="element-280">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register" data-id="element-281">
                  <Button variant="primary" size="sm" data-id="element-282">
                    {t('nav.getStarted')}
                  </Button>
                </Link>
              </div>}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2" data-id="element-283">
            {/* Mobile Language Switcher */}
            <div className="relative" ref={langRef} data-id="element-284">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-1 px-2 py-1.5 text-gray-600" data-id="element-285">
                <span className="text-lg" data-id="element-286">
                  {language === 'ar' ? '🇸🇦' : '🇺🇸'}
                </span>
                <ChevronDown size={14} className={`transition-transform ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-287" />
              </button>

              <AnimatePresence data-id="element-288">
                {isLangOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.15
              }} className="absolute top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden min-w-[120px]" style={{
                [language === 'ar' ? 'right' : 'left']: 0
              }} data-id="element-289">
                    <button onClick={() => handleLanguageChange('ar')} className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${language === 'ar' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-290">
                      <span data-id="element-291">🇸🇦</span>
                      <span data-id="element-292">العربية</span>
                    </button>
                    <button onClick={() => handleLanguageChange('en')} className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${language === 'en' ? 'text-[#4f1964] bg-purple-50' : 'text-gray-700'}`} data-id="element-293">
                      <span data-id="element-294">🇺🇸</span>
                      <span data-id="element-295">English</span>
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" data-id="element-296">
              <Menu size={24} data-id="element-297" />
            </button>
          </div>
        </div>
      </div>
    </nav>;
}